import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Modal, Fade, Typography } from '@mui/material';
import { getCvBlob } from '../../../services/FileService';
import NoteAdd from '@mui/icons-material/NoteAdd';
import RateReview from '@mui/icons-material/RateReview';
import Close from '@mui/icons-material/Close';
import LoadingButton from "@mui/lab/LoadingButton";
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { addCvCandidat } from '../../../services/CandidatService';
import PdfPreview from "./PDFPreview";

// Upload buttons shown in the candidate hero toolbar. `type` is the uploaded_file category.
const UPLOAD_BUTTONS = [
    { type: 'cv', tooltip: 'Ajouter un CV', successMsg: 'CV ajouté!', icon: <NoteAdd color="primary" />, pdfOnly: true },
    { type: 'cvFormate', tooltip: 'Ajouter un CV Formaté', successMsg: 'CV formaté ajouté!', icon: <NoteAdd color="action" />, pdfOnly: true },
    { type: 'autrePiece', tooltip: 'Ajouter un retour testeur', successMsg: 'Retour testeur ajouté!', icon: <RateReview />, pdfOnly: false },
];
const ANY_DOC_ACCEPT = '.pdf,.doc,.docx,.odt,.txt,.png,.jpg,.jpeg';

const paperStyle = {
    backgroundColor: '#fff',
    boxShadow: '0 24px 80px rgba(15,23,42,0.32)',
    borderRadius: '12px',
    // Large on desktop, almost full width on phones (was min 900px).
    width: 'min(96vw, max(82vw, 900px))',
    maxWidth: '1320px',
    height: '94vh',
    outline: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
};

function ShowCvCandidat({ snackbarShowMessage, files, idCandidat, editMode = false, reloadData, setReloadData }) {
    const [open, setOpen] = useState(false);
    const [loadingCV, setLoadingCV] = useState(false);
    const [iframe, setIframe] = useState('');
    const [uploadType, setUploadType] = useState(null);
    const searchCv = files?.filter((cv) => cv.categorie === "cv");
    const [cvUpload, setCvUpload] = useState(null);
    const uploadRef = useRef(null);

    const resetData = () => {
        setCvUpload(null);
        setUploadType(null);
        if (uploadRef.current) uploadRef.current.value = '';
    };

    useEffect(() => {
        const sendCv = async () => {
            if (cvUpload) {
                setLoadingCV(true);
                const formData = new FormData();
                formData.append('cv', cvUpload);
                formData.append('idCandidat', idCandidat);
                formData.append('type', uploadType);
                const button = UPLOAD_BUTTONS.find((b) => b.type === uploadType);
                try {
                    snackbarShowMessage('Ajout du fichier, patientez svp!', 'warning', 4000);
                    await addCvCandidat(formData);
                    snackbarShowMessage(button?.successMsg || 'Fichier ajouté!', 'success', 4000);
                    setReloadData(!reloadData);
                } catch (e) {
                    snackbarShowMessage("Erreur d'ajout du fichier.", 'error', 4000);
                } finally {
                    resetData();
                    setLoadingCV(false);
                }
            }
        };
        sendCv();
    }, [cvUpload]);

    const handleShowButton = () => {
        return (
            <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
                {!editMode &&
                    UPLOAD_BUTTONS.map((button) => (
                        <Tooltip key={button.type} title={button.tooltip}>
                            <span>
                                <LoadingButton
                                    onClick={() => {
                                        setUploadType(button.type);
                                        uploadRef.current.accept = button.pdfOnly ? '.pdf' : ANY_DOC_ACCEPT;
                                        uploadRef.current.click();
                                    }}
                                    variant="text"
                                    size="medium"
                                    loading={loadingCV}
                                    loadingPosition="start"
                                    startIcon={button.icon}
                                />
                            </span>
                        </Tooltip>
                    ))}
            </Box>
        );
    };

    const getIframe = async (cv) => {
        const res = await getCvBlob(cv.id ?? cv.image_name ?? cv.imageName);
        const fileUrl = window.URL.createObjectURL(res.data);
        const filename = cv.image_name ?? cv.imageName;
        return (
            <div style={{ height: '100%', display: 'flex', flex: 1, minHeight: 0 }}>
                <PdfPreview fileUrl={fileUrl} fileName={filename} />
            </div>
        );
    };

    const handleOpen = async () => {
        try {
            const iframe = await getIframe(searchCv[0]);
            setIframe(iframe);
            setOpen(true);
        } catch (e) {
            snackbarShowMessage("Impossible de charger le cv, fichier physique introuvable", "error", 4000);
        }
    };

    const handleClose = () => setOpen(false);

    const body = (
        <div style={paperStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44, padding: '0 14px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Fira Sans', sans-serif" }}>
                    CV CANDIDAT
                </Typography>
                <IconButton
                    aria-label="Fermer le CV"
                    onClick={handleClose}
                    size="small"
                    sx={{ color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', '&:hover': { background: '#e2e8f0' } }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </div>
            <div style={{ flex: 1, minHeight: 0, background: '#242424', padding: '18px 22px', overflow: 'hidden' }}>
                {iframe}
            </div>
        </div>
    );

    return (
        <>
            {handleShowButton()}
            <Modal
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, md: 2 } }}
                closeAfterTransition
                open={open}
                onClose={handleClose}
                BackdropProps={{ sx: { backgroundColor: 'rgba(15,23,42,0.62)', backdropFilter: 'blur(3px)' } }}
            >
                <Fade in={open}>{body}</Fade>
            </Modal>
            <input
                type="file"
                onChange={(e) => {
                    const file = e.target.files[0];
                    const pdfOnly = UPLOAD_BUTTONS.find((b) => b.type === uploadType)?.pdfOnly ?? true;
                    if (file && pdfOnly && file.type !== 'application/pdf') {
                        snackbarShowMessage('Veuillez télécharger uniquement des fichiers PDF.', 'error', 4000);
                        return;
                    }
                    setCvUpload(file);
                }}
                accept=".pdf"
                style={{ display: "none" }}
                ref={uploadRef}
                multiple
            />
        </>
    );
}

export default withSnackbar(ShowCvCandidat);
